import React, { useState } from "react";
import PropTypes from "prop-types";
import SectionCard from "../../SectionCard";

// Bios longer than this collapse behind a "see more" toggle (Figma's About excerpts run ~500-650).
const BIO_CHAR_LIMIT = 750;

/**
 * AboutSection — the shared profile "About" section card (chrome; the text comes from the engine).
 *
 * A `<SectionCard>` whose body is the entity's free-text bio, rendered with preserved line breaks.
 * Long bios collapse behind a "see more"/"see less" toggle; short bios render in full with no toggle.
 * When there is no text it shows muted empty-state copy instead. Model-agnostic: it takes a plain
 * string and the standard edit-affordance props, which pass straight through to `<SectionCard>`.
 * The section always renders its card — deciding whether to show it at all is the caller's job.
 *
 * @param {Object} props
 * @param {string} [props.text] - The bio text (falsy → empty-state copy)
 * @param {string} [props.title] - Section title (default "About")
 * @param {string} [props.emptyText] - Copy shown when there is no text (default "No bio added yet.")
 * @param {boolean} [props.editable] - Whether the viewer may edit this section
 * @param {Function} [props.onEdit] - Click handler for the standard edit pen (shown only when editable)
 * @param {string} [props.editButtonId] - Optional DOM id for the edit pen (stable hook for system tests)
 * @param {string} [props.id] - Optional DOM id (e.g. for system tests / deep links)
 * @param {string} [props.eyebrowLabel] - Label for an optional chip row shown above the bio (e.g. "Covers:")
 * @param {React.ReactNode} [props.eyebrowContent] - The caller's already-rendered chip row; the whole
 *   eyebrow block (label, chips, divider) is omitted when this is falsy
 */
function AboutSection({
  text = undefined,
  title = "About",
  emptyText = "No bio added yet.",
  editable = false,
  onEdit = null,
  editButtonId = undefined,
  id = undefined,
  eyebrowLabel = null,
  eyebrowContent = null,
}) {
  const [expanded, setExpanded] = useState(false);
  // Count/slice by code point (not UTF-16 unit) so a boundary never splits an emoji surrogate
  // pair. Trailing horizontal whitespace is trimmed before the ellipsis, but newlines are kept
  // so the collapsed excerpt honours the same pre-wrap line breaks as the full text.
  const chars = text ? Array.from(text) : [];
  const shouldTruncate = chars.length > BIO_CHAR_LIMIT;
  const collapsedBio = `${chars
    .slice(0, BIO_CHAR_LIMIT)
    .join("")
    .replace(/[ \t]+$/, "")}…`;

  return (
    <SectionCard id={id} title={title} editable={editable} onEdit={onEdit} editButtonId={editButtonId}>
      {eyebrowContent && (
        <>
          <div className="paragraph-xs fw-semibold lh-sm text-neutral-50 text-uppercase mb-2">{eyebrowLabel}</div>
          {/* The design's rhythm is an even 16px on both sides of the divider (hr my-3); the chip
              row carries no margin of its own. */}
          <div>{eyebrowContent}</div>
          <hr className="my-3" />
        </>
      )}
      {text ? (
        <div style={{ whiteSpace: "pre-wrap" }} className="text-break paragraph-md">
          {shouldTruncate ? (
            <>
              {expanded ? text : collapsedBio}{" "}
              <button
                type="button"
                className="btn btn-link p-0 align-baseline text-primary border-0"
                style={{ font: "inherit" }}
                aria-expanded={expanded}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded ? "see less" : "see more"}
              </button>
            </>
          ) : (
            text
          )}
        </div>
      ) : (
        <div className="text-muted">{emptyText}</div>
      )}
    </SectionCard>
  );
}

AboutSection.propTypes = {
  text: PropTypes.string,
  title: PropTypes.string,
  emptyText: PropTypes.string,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  editButtonId: PropTypes.string,
  id: PropTypes.string,
  eyebrowLabel: PropTypes.string,
  eyebrowContent: PropTypes.node,
};

export default AboutSection;
